import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { LoginUser } from '@app/core/models/user.model';
import { TranslateService } from '@ngx-translate/core';
import MetisMenu from 'metismenujs';

import { AuthenticationService } from '@app/core/services/auth.service';
import {
  AdminMenuItems,
  ContactMenuItem,
  ProgramMenuItem,
  ActivityMenuItem,
  EventsMenuItem,
  DashboardMenuItem,
  FormMenuItem,
  prepareMenuItem,
  ScheduleMenuItem,
  QuestionnaireMenuItem,
  ReportMenuItem,
  OrganisationMenuItem,
  FamilyMenuItem,
  WaitingListMenuItem,
  ActionPlanMenuItem,
  NonCaseLoadMenuItem,
  HelplineMenuItem,
  HighRiskAlertsMenuItem,
  PropertiesMenuItem,
  ApprovalsMenuItem
} from './menu';
import { MenuItem, MenuNode } from './menu.model';
import { App } from '@app/app.helper';
import { AlertNotificationService } from '@app/services/form-triggers/alert-notification.service';
import { ApprovalNotificationService } from '@app/services/form-triggers/approval-notification.service';
import { ReportsService } from '@app/services/reports/reports.service';
import { TerminologyService } from '@app/services/terminology/terminology.service';
import { SessionShareService } from '@app/services/sessions/session-share.service';



@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false
})

/**
 * Sidebar component
 */
export class SidebarComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('componentRef') scrollRef;
  @Input() isCondensed = false;

  public user: LoginUser;
  menu: any;

  // Every stream this component listens to. The sidebar is destroyed and re-created each time a
  // record page toggles full-screen mode (*ngIf="!hideMenu" in the vertical layout), so without
  // tearing these down every record visit left a dead instance still reacting to user, feed and
  // terminology emissions - re-fetching the sharing switch and rebuilding a menu nobody could see.
  // With the per-request token rotation that used to emit currentUser, the number of sharing
  // calls per request grew by one for every record page ever opened in the session.
  private readonly subs = new Subscription();

  public menuItems: MenuItem[] = [];
  private menuItemsMap: Map<string, MenuNode> = new Map();

  /** Project session-sharing switch — re-read on every menu build, never cached from the token. */
  private sessionSharingEnabled = false;

  @ViewChild('sideMenu') sideMenu: ElementRef;

  constructor(
    router: Router,
    public translate: TranslateService,
    private authService: AuthenticationService,
    private reportsService: ReportsService,
    private terminologyService: TerminologyService,
    private sessionShareService: SessionShareService,
    private alertNotificationService: AlertNotificationService,
    private approvalNotificationService: ApprovalNotificationService
  ) {
    this.subs.add(router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this._activateMenuDropdown();
        this._scrollElement();
      }
    }));

    this.subs.add(this.authService.currentUserObs.subscribe((user) => {
      if (user) {
        this.user = user;
        // Resolve the sharing switch before building the menu, then rebuild once it lands so the
        // Sharing item appears/disappears on a page refresh rather than only at next sign-in.
        this.refreshSharingSwitch();
        this.initialize();
      }
    }));

    // Saving the project settings can flip the sharing switch — rebuild the menu straight away so
    // the Sharing item appears (or goes) on save, without waiting for a refresh.
    this.subs.add(this.sessionShareService.sharingSwitchChanged$.subscribe(() => this.refreshSharingSwitch()));

    // The feeds push their count and availability on EVERY refresh (each navigation, at most
    // every 10 s) whether or not the value moved, and each push below rebuilds the whole menu.
    // distinctUntilChanged keeps the rebuild for genuine changes only - the value is a number
    // or a boolean, so equality is exact - instead of four rebuilds per navigation for nothing.

    // Live open-alert count bubble on the High-Risk Alerts entry; cleared at zero.
    this.subs.add(this.alertNotificationService.openCount$.pipe(distinctUntilChanged()).subscribe((count) => {
      const node = HighRiskAlertsMenuItem[1];
      if (count > 0) node.badge = { variant: 'danger', text: String(count) };
      else delete node.badge;
      if (this.user) this.initialize();
    }));
    // Visibility is server-decided (admins always; others only while assigned to a
    // trigger) - rebuild the menu when it changes so the entry appears/disappears.
    this.subs.add(this.alertNotificationService.available$.pipe(distinctUntilChanged()).subscribe(() => {
      if (this.user) this.initialize();
    }));

    // Approvals: same pattern as the alerts entry — live pending-count bubble + server-decided
    // visibility, on its own feed (independent gate and audience).
    this.subs.add(this.approvalNotificationService.pendingCount$.pipe(distinctUntilChanged()).subscribe((count) => {
      const node = ApprovalsMenuItem[1];
      if (count > 0) node.badge = { variant: 'danger', text: String(count) };
      else delete node.badge;
      if (this.user) this.initialize();
    }));
    this.subs.add(this.approvalNotificationService.available$.pipe(distinctUntilChanged()).subscribe(() => {
      if (this.user) this.initialize();
    }));

    // Terminology arrives asynchronously after login (load() fires from app.component).
    // When it lands — or whenever the admin saves an override — re-run initialize()
    // so the menu rebuilds with the resolved labels. Without this, the sidebar shows
    // the canonical defaults because the first initialize() ran before the bundle
    // had loaded.
    this.subs.add(this.terminologyService.catalogue().subscribe(() => {
      if (this.user) this.initialize();
    }));
  }

  ngOnInit() {
    this._scrollElement();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.menu?.dispose?.();
  }

  /** Re-read the project sharing switch and rebuild the menu with the result. */
  private refreshSharingSwitch(): void {
    this.sessionShareService.getSharingEnabled().subscribe({
      next: (info) => {
        // The Sharing page is the history, which is Licence/System Admin only — so the menu item
        // follows who may READ it, not merely whether sharing is on.
        this.sessionSharingEnabled = !!info?.sessionSharingEnabled && !!info?.canViewAudit;
        this.initialize();
      },
      error: () => {
        this.sessionSharingEnabled = false;
        this.initialize();
      }
    });
  }

  ngAfterViewInit() {
    this.menu = new MetisMenu(this.sideMenu.nativeElement);
    this._activateMenuDropdown();
  }

  toggleMenu(event) {
    event.currentTarget.nextElementSibling.classList.toggle('mm-show');
  }

  ngOnChanges() {
    if ((!this.isCondensed && this.sideMenu) || this.isCondensed) {
      setTimeout(() => {
        this.menu = new MetisMenu(this.sideMenu.nativeElement);
      });
    } else if (this.menu) {
      this.menu.dispose();
    }
  }
  _scrollElement() {
    setTimeout(() => {
      if (document.getElementsByClassName('mm-active').length > 0) {
        const currentPosition =
          document.getElementsByClassName('mm-active')[0]['offsetTop'];
        if (currentPosition > 500)
          if (this.scrollRef.SimpleBar !== null)
            this.scrollRef.SimpleBar.getScrollElement().scrollTop =
              currentPosition + 300;
      }
    }, 300);
  }

  /**
   * remove active and mm-active class
   */
  _removeAllClass(className) {
    const els = document.getElementsByClassName(className);
    while (els[0]) {
      els[0].classList.remove(className);
    }
  }

  /**
   * Activate the parent dropdown
   */
  _activateMenuDropdown() {
    this._removeAllClass('mm-active');
    this._removeAllClass('mm-show');
    const links = document.getElementsByClassName('side-nav-link-ref');
    let menuItemEl = null;
    // tslint:disable-next-line: prefer-for-of
    const paths = [];
    for (let i = 0; i < links.length; i++) {
      paths.push(links[i]['pathname']);
    }
    var itemIndex = paths.indexOf(window.location.pathname);
    if (itemIndex === -1) {
      const strIndex = window.location.pathname.lastIndexOf('/');
      const item = window.location.pathname.substr(0, strIndex).toString();
      menuItemEl = links[paths.indexOf(item)];
    } else {
      menuItemEl = links[itemIndex];
    }
    if (menuItemEl) {
      menuItemEl.classList.add('active');
      const parentEl = menuItemEl.parentElement;
      if (parentEl) {
        parentEl.classList.add('mm-active');
        const parent2El = parentEl.parentElement.closest('ul');
        if (parent2El && parent2El.id !== 'side-menu') {
          parent2El.classList.add('mm-show');
          const parent3El = parent2El.parentElement;
          if (parent3El && parent3El.id !== 'side-menu') {
            parent3El.classList.add('mm-active');
            const childAnchor = parent3El.querySelector('.has-arrow');
            const childDropdown = parent3El.querySelector('.has-dropdown');
            if (childAnchor) {
              childAnchor.classList.add('mm-active');
            }
            if (childDropdown) {
              childDropdown.classList.add('mm-active');
            }
            const parent4El = parent3El.parentElement;
            if (parent4El && parent4El.id !== 'side-menu') {
              parent4El.classList.add('mm-show');
              const parent5El = parent4El.parentElement;
              if (parent5El && parent5El.id !== 'side-menu') {
                parent5El.classList.add('mm-active');
                const childanchor = parent5El.querySelector('.is-parent');
                if (childanchor && parent5El.id !== 'side-menu') {
                  childanchor.classList.add('mm-active');
                }
              }
            }
          }
        }
      }
    }
  }

  /**
   * Initialize
   */
  initialize(): void {
    const menuItems = [];
    const nonAdminMap = new Map<string, MenuNode>(
      this.prepareNonAdminMenuArr(this.user)
    );
    const nonAdminMenuItems = this.prepareMenuItems(nonAdminMap);
    menuItems.push(...nonAdminMenuItems);

    // The Sharing page is part of the session-sharing framework, so it follows the project's
    // master switch: with sharing off the whole feature is invisible. The switch itself lives on
    // Update Project (Modules → Session Sharing), NOT here, so there is still a way to turn it
    // back on once this item has gone.
    // Admin Risk Alerts (trigger config) likewise follows the project's Risk Alerts module
    // toggle - configuring triggers on a project that can never raise them only misleads
    // (testers created triggers here with the module off and saw nothing fire). The toggle
    // itself lives on Update Project (Modules → Risk Alerts).
    const adminMap = new Map(
      AdminMenuItems.filter(([key]) =>
        (this.sessionSharingEnabled || key !== 'MENUITEMS.SHARINGAUDIT.TEXT')
        && (this.user?.riskAlertsEnabled || this.user?.workflowEventsEnabled || key !== 'MENUITEMS.RISKALERTS.TEXT'))
    );
    const adminMenuItems = this.prepareMenuItems(adminMap);
    if (adminMenuItems.length > 1) {
      menuItems.push(...adminMenuItems);
    }

    this.menuItems = menuItems;

    // Rebuilding menuItems re-renders the menu *ngFor, discarding the DOM nodes that
    // carried the mm-active highlight classes (the colour rule keys off `.mm-active > a`,
    // not routerLinkActive's `.active`). initialize() re-runs after first paint - user
    // re-emissions, terminology bundle load - so reapply the highlight once the new
    // nodes have rendered, or the menu silently loses its active state.
    setTimeout(() => this._activateMenuDropdown());
  }

  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasItems(item: MenuItem) {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }

  private prepareMenuItems(menuMap: Map<string, MenuNode>) {
    const menuItems = [];
    menuMap.forEach((menuNode, key) => {
      // System-Admin-only items are hidden for everyone else (route is also guarded).
      if (menuNode.isSystemAdmin && !this.user?.isSystemAdmin) {
        return;
      }
      // Project-Admin-and-above items are hidden for lower roles (route is also guarded).
      if (menuNode.isProjectAdminOrAbove && !this.user?.isProjectAdminOrAbove) {
        return;
      }
      const item = prepareMenuItem(key, menuMap);
      if (item) {
        menuItems.push(item);
      }
    });
    return menuItems;
  }

  /**
   * Overwrites the menu item's label with the licence's configured terminology IF
   * the terminology bundle has loaded for that key. Until the bundle arrives we
   * leave the original i18n key alone so the translate pipe still produces the
   * canonical label — preventing the menu from briefly showing the raw term key
   * (e.g. `"programme_plural"`) on first paint.
   */
  private applyTerminologyLabel(menuItem: [string, MenuNode], termKey: string): void {
    const resolved = this.terminologyService.getAllResolved();
    if (resolved && Object.prototype.hasOwnProperty.call(resolved, termKey) && resolved[termKey]) {
      menuItem[0] = resolved[termKey];
    }
  }

  private prepareNonAdminMenuArr(user: LoginUser) {
    const mapArr: Array<[string, MenuNode]> = [];
    mapArr.push(DashboardMenuItem);
    mapArr.push(ScheduleMenuItem);
    mapArr.push(FormMenuItem);
    mapArr.push(ContactMenuItem);
    ContactMenuItem[0] = App.contactLabelPlural;
    // Override the i18n-default menu labels with admin-configured terminology, but
    // only when terminology has actually loaded — otherwise leave the original i18n
    // key in place so the menu still renders correctly through the translate pipe.
    // The catalogue subscription in the constructor re-runs initialize() once the
    // bundle arrives.
    this.applyTerminologyLabel(ProgramMenuItem, 'programme_plural');
    this.applyTerminologyLabel(ActivityMenuItem, 'activity_plural');
    // Legacy modules: default ON; the per-project switch (Update Project → Modules) hides them.
    if (user?.waitingListEnabled !== false) {
      mapArr.push(WaitingListMenuItem);
    }
    // With the Support Plan module on, objectives live on the client record (the Support Assessment
    // tab) and the project-wide list is kept off the menu and its route (Ronan 2026-10-14); the page
    // itself stays for a project that only runs plain action plans.
    if (user?.actionPlanEnabled && !user?.supportPlanEnabled) {
      mapArr.push(ActionPlanMenuItem);
      this.applyTerminologyLabel(ActionPlanMenuItem, 'action_plan_plural');
    }
    if (user?.nonCaseLoadEnabled) {
      mapArr.push(NonCaseLoadMenuItem);
    }
    if (user?.helplineEnabled) {
      mapArr.push(HelplineMenuItem);
    }
    if (user?.riskAlertsEnabled && this.alertNotificationService.available$.value) {
      mapArr.push(HighRiskAlertsMenuItem);
    }
    if (user?.propertiesEnabled) {
      mapArr.push(PropertiesMenuItem);
      this.applyTerminologyLabel(PropertiesMenuItem, 'property_plural');
    }
    if (user?.workflowEventsEnabled && this.approvalNotificationService.available$.value) {
      mapArr.push(ApprovalsMenuItem);
    }
    if (user?.familyEnabled !== false) {
      mapArr.push(FamilyMenuItem);
    }
    if (user?.organisationEnabled !== false) {
      mapArr.push(OrganisationMenuItem);
    }
    mapArr.push(ProgramMenuItem);
    mapArr.push(EventsMenuItem);
    mapArr.push(ActivityMenuItem);
    mapArr.push(QuestionnaireMenuItem);
    mapArr.push(ReportMenuItem);

    return mapArr;
  }
}
